/**
 * O tratador dos eventos do WebSocket do portal (D3, passo 19 — PR 2): o que cada evento faz no cache do react-query e no aviso.
 * Fora do React e testável sem componente; o `PortalAoVivo` o chama. Os formatos são os do PR 1 e a leitura é defensiva
 * (`eventosDoPortal.ts`): evento torto é ignorado.
 *
 * Regras (decisões 5 e 6 do plano):
 *  - o SERVIDOR é a verdade do sino: `notification:new` e a leitura invalidam (o `invalidateQueries` cancela o GET em voo e refaz); o
 *    `badge_update` do conectar DEFINE a contagem e refaz a lista; o de leitura só BAIXA a contagem;
 *  - resposta do atendente: soma 1 (otimista) no contador salvo se a conversa está À VISTA, e em seguida INVALIDA o `/nao-lidas`: o `setQueryData`
 *    não cancela um GET já em voo (o do `refazerTudo` ao entrar em `ws` ou ao voltar a aba), e se ele leu antes do commit da resposta apagaria o
 *    +1 — com o socket vivo não há polling, então o erro duraria até a próxima reconexão. A invalidação cancela a busca velha e refaz: o número
 *    sobe na hora e o servidor confirma. Refaz também a lista e o poll da sessão SEMPRE. O servidor marca
 *    `read_at` em toda busca, mas a busca feita com a aba escondida vai com `marcar_lidas=false` (`paramsDeLeitura`, PR 1 Decisão 10), então
 *    não marca como lida uma resposta que o parceiro não viu; ao voltar a aba o `PortalAoVivo` refaz o poll sem o parâmetro e é isso que a "abre";
 *  - sem `portal:atendimento:view` os dois eventos de atendimento são ignorados;
 *  - 🔴 uma exceção de um evento NUNCA sai daqui: o chamador é o `onMessage` do socket, e o que lança lá dentro não pode derrubar o
 *    socket nem os eventos seguintes. O cache de `/nao-lidas` torto (sem `por_sessao`) faz `somarRespostaNova` lançar `TypeError`: esse
 *    passo é isolado (as buscas, inclusive a do `/nao-lidas`, são refeitas mesmo assim — e elas consertam o cache — e o aviso sai), e o `tratarEventoDoPortal`
 *    ainda tem a rede de segurança de fora.
 */
import type { QueryClient } from "@tanstack/react-query";

import type { AcoesDoAviso, AvisoParaMostrar } from "@/components/notifications/AvisoNaTela";
import { invalidarAtendimentoDoPortal } from "@/hooks/usePortalAtendimento";
import { COUNT_KEY, LIST_KEY, invalidarNotificacoesDoPortal } from "@/hooks/usePortalNotifications";
import { anunciar } from "@/lib/avisos/avisador";
import { anunciarAtendimento } from "@/lib/avisos/avisoDoAtendimento";
import { salaAbertaAVista } from "@/lib/avisos/salaAberta";
import {
  TIPOS_DO_PORTAL,
  lerBadge,
  lerLidas,
  lerMensagemNova,
  lerNotificacaoNova,
  type MensagemNovaDoAtendimento,
} from "@/lib/portal/eventosDoPortal";
import {
  CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO,
  aplicarLidas,
  somarRespostaNova,
} from "@/lib/portal/naoLidasDoAtendimento";
import { descricaoDaSessao } from "@/lib/sessaoDisplay";

export interface ContextoDoAoVivo {
  qc: QueryClient;
  /** O id do parceiro logado: o evento é do canal dele, o filtro é defesa. */
  userId: string | null;
  /** `portal:atendimento:view`. */
  podeAtendimento: boolean;
  mostrarAviso: (aviso: AvisoParaMostrar, acoes: AcoesDoAviso) => void;
  acoes: AcoesDoAviso;
  /** Ids já tratados (o mesmo evento não avisa duas vezes). */
  vistos: Set<string>;
}

const LIMITE_DE_VISTOS = 200;

/** Já tratei esta chave? Se não, lembra (esquece a mais antiga passando de 200). */
export function jaVisto(vistos: Set<string>, chave: string): boolean {
  if (vistos.has(chave)) return true;
  vistos.add(chave);
  if (vistos.size > LIMITE_DE_VISTOS) {
    const maisAntigo = vistos.values().next().value;
    if (maisAntigo !== undefined) vistos.delete(maisAntigo);
  }
  return false;
}

/** O item do aviso na tela para uma resposta do atendente. O `id` é o da MENSAGEM (nunca vai para `marcarLida`). */
export function avisoDaResposta(m: MensagemNovaDoAtendimento): AvisoParaMostrar {
  return {
    id: m.message_id,
    title: descricaoDaSessao({
      id: m.session_id,
      sector_name: m.setor,
      status: "IN_PROGRESS",
      attendant_first_name: m.author_name,
    }),
    body: m.preview,
    severity: "info",
    categoria: "portal_atendimento",
    link: `/portal/atendimento?sessao=${encodeURIComponent(m.session_id)}`,
    created_at: m.created_at || new Date().toISOString(),
    atendimento: { session_id: m.session_id, setor: m.setor, author_name: m.author_name },
  };
}

/** Roda um passo que mexe no cache: se lançar, avisa no log e segue (o resto do evento ainda acontece). */
function isolar(passo: string, fazer: () => void): void {
  try {
    fazer();
  } catch (erro) {
    console.warn(`[portal] ${passo} falhou:`, erro);
  }
}

function aoBadge(dados: unknown, ctx: ContextoDoAoVivo): void {
  const b = lerBadge(dados);
  if (!b) return;
  const deLeitura = b.todas || b.lidas !== null;
  ctx.qc.setQueryData<number>(COUNT_KEY, (atual) =>
    deLeitura && typeof atual === "number" ? Math.min(atual, b.unread_count) : b.unread_count
  );
  // O do conectar perdeu o que veio durante a queda; o de leitura traz itens que mudaram de estado.
  void ctx.qc.invalidateQueries({ queryKey: LIST_KEY });
}

function aoNotificacaoNova(dados: unknown, ctx: ContextoDoAoVivo): void {
  const n = lerNotificacaoNova(dados);
  if (!n) return;
  if (n.user_id && ctx.userId && n.user_id !== ctx.userId) return;
  if (jaVisto(ctx.vistos, n.id)) return;

  invalidarNotificacoesDoPortal(ctx.qc);
  anunciar({ aviso: n.aviso, severity: n.severity }, () =>
    ctx.mostrarAviso(
      {
        id: n.id,
        title: n.title,
        body: n.body,
        severity: n.severity,
        categoria: n.categoria,
        link: n.link,
        created_at: n.created_at ?? new Date().toISOString(),
      },
      ctx.acoes
    )
  );
}

function aoMensagemNova(dados: unknown, ctx: ContextoDoAoVivo): void {
  if (!ctx.podeAtendimento) return;
  const m = lerMensagemNova(dados);
  if (!m) return;
  if (m.user_id && ctx.userId && m.user_id !== ctx.userId) return;
  if (jaVisto(ctx.vistos, `msg:${m.message_id}`)) return;

  // À vista: a busca abaixo marca lida e o servidor confirma por `atendimento:lidas` — somar aqui acenderia o selo por um instante.
  if (!salaAbertaAVista(m.session_id)) {
    isolar("somar a resposta nova às não lidas", () => somarRespostaNova(ctx.qc, m.session_id));
    // 🔴 Depois do +1, também quando ele falhou (cache torto): o `setQueryData` não cancela um GET em voo, que se leu antes do commit apagaria o
    // +1. A invalidação cancela a busca velha e refaz; o número sobe na hora e o servidor confirma.
    isolar("refazer as não lidas", () => {
      void ctx.qc.invalidateQueries({ queryKey: CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO });
    });
  }
  // Sempre a lista e o poll da sessão (só a conversa aberta tem observador). Aba escondida: a busca vai com `marcar_lidas=false`.
  invalidarAtendimentoDoPortal(ctx.qc, m.session_id);
  anunciarAtendimento(m, () => ctx.mostrarAviso(avisoDaResposta(m), ctx.acoes));
}

function aoLidas(dados: unknown, ctx: ContextoDoAoVivo): void {
  if (!ctx.podeAtendimento) return;
  const l = lerLidas(dados);
  if (!l) return;
  if (l.user_id && ctx.userId && l.user_id !== ctx.userId) return;
  isolar("aplicar as lidas às não lidas", () => aplicarLidas(ctx.qc, l.session_id, l.nao_lidas));
}

/**
 * Trata UM evento do socket. Nunca lança: evento torto é ignorado e uma exceção vira aviso no log (ver o cabeçalho).
 */
export function tratarEventoDoPortal(evento: unknown, ctx: ContextoDoAoVivo): void {
  if (typeof evento !== "object" || evento === null) return;
  const { type, data } = evento as { type?: unknown; data?: unknown };
  try {
    switch (type) {
      case TIPOS_DO_PORTAL.badge:
        aoBadge(data, ctx);
        return;
      case TIPOS_DO_PORTAL.nova:
        aoNotificacaoNova(data, ctx);
        return;
      case TIPOS_DO_PORTAL.mensagemNova:
        aoMensagemNova(data, ctx);
        return;
      case TIPOS_DO_PORTAL.lidas:
        aoLidas(data, ctx);
        return;
      default:
        // `pong` e o que o servidor passar a mandar: ignorado.
        return;
    }
  } catch (erro) {
    console.warn(`[portal] o evento ${String(type)} falhou:`, erro);
  }
}
